import React, { useState } from 'react'
import Button from './Button'
import { resendConfirmation } from '../api'

// Renders a banner when a client's email is not deliverable, driven by the
// client's email_status from the backend:
//   ok          → no banner
//   unconfirmed → "confirm your email" prompt
//   bounced     → "we couldn't deliver" (e.g. departed-employee mailbox dead)
//   complaint   → gentle "filtered as spam, please re-add us & re-confirm"
//
// Props: status, detail, email.
const CONFIG = {
  unconfirmed: {
    tone: 'amber',
    title: 'Confirm your email to start your digest',
    fallback:
      'Your email hasn’t been confirmed yet, so no digest is being sent. Resend the confirmation link below.',
    action: 'resend',
  },
  bounced: {
    tone: 'red',
    title: 'We couldn’t deliver your last digest',
    fallback:
      'Your digest emails are bouncing. The mailbox may no longer be active (for example, if the person who set this up has left). Please update the email on your account so you don’t miss any grants.',
    action: 'resend',
  },
  complaint: {
    tone: 'amber',
    title: 'Your digest may be going to spam',
    fallback:
      'It looks like our last digest was filtered as spam. To keep receiving it, add digest@praenum.com to your contacts and confirm your email again below.',
    action: 'resend',
  },
}

const TONE = {
  amber: 'bg-amber-50 border-amber-200 text-amber-800',
  red: 'bg-red-50 border-red-200 text-red-800',
}

export default function EmailHealthBanner({ status, detail, email }) {
  const [loading, setLoading] = useState(false)
  const [msg, setMsg] = useState('')

  if (!status || status === 'ok') return null
  const cfg = CONFIG[status]
  if (!cfg) return null

  async function handleResend() {
    setLoading(true)
    setMsg('')
    try {
      const res = await resendConfirmation(email)
      setMsg(res.message || 'A new confirmation link has been sent.')
    } catch (err) {
      setMsg(err.message || 'Could not send right now. Please try again shortly.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={`rounded-lg border p-4 ${TONE[cfg.tone]}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-semibold text-sm">{cfg.title}</p>
          <p className="mt-1 text-sm">{detail || cfg.fallback}</p>
          {msg && <p className="mt-2 text-sm font-medium">{msg}</p>}
        </div>
        {cfg.action === 'resend' && email && (
          <Button
            size="sm"
            variant="secondary"
            loading={loading}
            onClick={handleResend}
            className="shrink-0"
          >
            Resend confirmation
          </Button>
        )}
      </div>
    </div>
  )
}
